import { ProductLabel } from "../../ui/product-label.tsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, q } from "../../api.ts";
import { number } from "../../format.ts";
import { AREA_LABELS, INBOX_LABELS as I, READING, readingLabel, reasonText } from "../../labels.ts";
import { MetricSummary } from "../../metrics.tsx";
import { Button, ErrorText, Kbd, Page, Pill, Reasons } from "../../ui.tsx";
import { Excerpt } from "../../ui/review.tsx";
import { Skeleton } from "../../ui/skeleton.tsx";
import { DetailPanelShell, InboxContext, Review, type ReviewMode, ReviewModeContext, useInbox } from "./shared.tsx";

// Part of the inbox (docs/design-system.md §0.4, plan M7 step 7g).

/**
 * A campaign's reading (plan M4 step 4d-2): what its metrics showed over its
 * window, its actions and their readings, the agent's draft, and the value
 * and next step the person confirms. Stopping closes the campaign.
 */
export function CampaignReadingReview() {
  const { campaignId } = useParams({ strict: false }) as { campaignId: string };
  const { items, intent, done } = useInbox();
  const review = useQuery(q.campaignReadingReview(campaignId));
  const item = items.find((entry) => entry.kind === "campaign_reading" && entry.id === campaignId);
  const [value, setValue] = useState<C.ReadingValueT | null>(null);
  const [step, setStep] = useState<"continue" | "adjust" | "stop" | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setValue(null); setStep(null); setNote(""); setError(undefined); }, [campaignId]);
  useEffect(() => {
    if (!review.data) return;
    const allows = (v: C.ReadingValueT, s: string) => review.data.allowed.some((a) => a.value === v && a.next_step === s && !a.refusal);
    const draft = review.data.draft;
    // Start from the agent's draft when the rules allow it.
    if (draft && allows(draft.value, draft.next_step)) { setValue((v) => v ?? draft.value); setStep((s) => s ?? draft.next_step); }
    if (intent === "approve") confirmRef.current?.focus();
  }, [intent, review.data, campaignId]);

  if (review.isLoading) return <DetailPanelShell><Skeleton shape="panel" /></DetailPanelShell>;
  if (!review.data) return <DetailPanelShell><ErrorText error={review.error} /></DetailPanelShell>;
  const data = review.data;
  const open = data.campaign.status === "active" && !data.reading;
  const refusalOf = (v: C.ReadingValueT, s: string) => data.allowed.find((a) => a.value === v && a.next_step === s)?.refusal ?? null;
  // A value is open when some next step allows it; a next step, when the chosen value allows it.
  const valueRefusal = (v: C.ReadingValueT) => (["continue", "adjust", "stop"] as const).every((s) => refusalOf(v, s)) ? refusalOf(v, "stop") : null;
  const stepRefusal = (s: "continue" | "adjust" | "stop") => (value ? refusalOf(value, s) : null);
  const measurement = data.measurement;
  const confirm = async () => {
    if (!value || !step) return;
    setBusy(true); setError(undefined);
    try {
      await api.post(`/api/campaign-readings/${encodeURIComponent(data.campaign.id)}/confirm`, { value, next_step: step, ...(note.trim() ? { note: note.trim() } : {}) });
      done(I.readingConfirmed);
    } catch (cause) { setError(cause); }
    finally { setBusy(false); }
  };
  return <Review label={I.detail} title={data.campaign.title}
    meta={<><Pill tone={open ? "orange" : "gray"} status>{open ? I.reason[measurement.measurement.matured ? "campaign_reading_due" : "campaign_reading_drafted"] : I.confirmedReading}</Pill>{item && <ProductLabel product={item.product} />}</>}
    actions={open && <Button ref={confirmRef} tone="primary" busy={busy} disabled={!value || !step || stepRefusal(step) !== null} onClick={() => void confirm()}>{I.confirmReading} <Kbd>A</Kbd></Button>}>
    <p className="z-small z-muted">{I.campaignReadingNote}</p>
    <dl className="z-props">
      <dt>{I.hypothesis}</dt><dd><Excerpt className="z-agent-text" lines={2}>{data.campaign.hypothesis}</Excerpt></dd>
      <dt>{I.window}</dt><dd>{I.windowDays(data.campaign.window_days)} · {measurement.measurement.matured ? I.phase.matured : I.phase.early}{measurement.window_start && <> · {I.windowStarted(measurement.window_start)}</>}{measurement.matures_at && !measurement.measurement.matured && <> · {I.maturesAt(measurement.matures_at)}</>}</dd>
    </dl>
    <section className="z-flow"><h3>{I.metrics}</h3><MetricSummary result={measurement} /></section>
    <section className="z-flow">
      <h3>{I.actions} <span className="z-count">{number(data.actions.length)}</span></h3>
      <ul className="z-points">{data.actions.map((a) => <li key={a.id}>{a.title} · <span className="z-muted">{I.actionKind[a.kind] ?? a.kind} · {a.reading ? readingLabel(a.reading, "observational") : I.noActionReading}</span></li>)}</ul>
    </section>
    <section className="z-flow">
      <h3>{I.rulesSuggest}</h3>
      <p>{measurement.suggestion.value ? <Pill tone={READING[measurement.suggestion.value].tone} status>{READING[measurement.suggestion.value].label}</Pill> : I.noSuggestion}</p>
      <Reasons reasons={measurement.suggestion.reasons} />
    </section>
    {data.draft && <section className="z-flow">
      <h3>{I.agentDraft}</h3>
      <p className="z-small z-muted">{I.agentDraftNote}</p>
      <p><Pill tone={READING[data.draft.value].tone} status>{READING[data.draft.value].label}</Pill> · {I.nextSteps[data.draft.next_step]}</p>
      <Excerpt className="z-agent-text" lines={3}>{data.draft.explanation}</Excerpt>
    </section>}
    {data.reading && <dl className="z-props"><dt>{I.confirmedReading}</dt><dd>{READING[data.reading.value].label} · {I.nextSteps[data.reading.next_step]}{data.reading.note && <> · <span className="z-agent-text">{data.reading.note}</span></>}</dd></dl>}
    {open && <>
      <div className="z-flow" role="radiogroup" aria-labelledby="campaign-reading-value">
        <h3 id="campaign-reading-value">{I.yourReading}</h3>
        <ul className="z-check">{(["positive", "no_signal", "negative", "inconclusive"] as const).map((v) => {
          const refusal = valueRefusal(v);
          return <li key={v}>
            <input id={`campaign-value-${v}`} type="radio" name="campaign-reading-value" checked={value === v} disabled={busy || refusal !== null} onChange={() => setValue(v)} />
            <label htmlFor={`campaign-value-${v}`}>{READING[v].label}{refusal && <span className="z-small z-muted"> · {reasonText({ code: refusal })}</span>}</label>
          </li>;
        })}</ul>
      </div>
      <div className="z-flow" role="radiogroup" aria-labelledby="campaign-reading-step">
        <h3 id="campaign-reading-step">{I.nextStep}</h3>
        <ul className="z-check">{(["continue", "adjust", "stop"] as const).map((s) => {
          const refusal = stepRefusal(s);
          return <li key={s}>
            <input id={`campaign-step-${s}`} type="radio" name="campaign-reading-step" checked={step === s} disabled={busy || refusal !== null} onChange={() => setStep(s)} />
            <label htmlFor={`campaign-step-${s}`}>{I.nextSteps[s]} · <span className="z-small z-muted">{refusal ? reasonText({ code: refusal }) : I.nextStepHelp[s]}</span></label>
          </li>;
        })}</ul>
      </div>
      <div className="z-field">
        <label htmlFor="campaign-reading-note">{I.readingNoteLabel}</label>
        <textarea id="campaign-reading-note" className="z-input" rows={2} maxLength={400} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} />
      </div>
    </>}
    <ErrorText error={error} />
  </Review>;
}

/** `?intent=approve`: the campaign page's A opens the reading with Confirm ready. */
export const campaignReadingSearch = (search: Record<string, unknown>): { intent?: "approve" } => (search.intent === "approve" ? { intent: "approve" } : {});

/**
 * A campaign's reading as its own page (design system §0.4a; plan M7 step
 * 7g): it spans the campaign's metrics and all its actions, so it gets the
 * width of a page. Esc and the decision return to the campaign.
 */
export function CampaignReadingPage() {
  const { productId, campaignId } = useParams({ strict: false }) as { productId: string; campaignId: string };
  const navigate = useNavigate();
  const client = useQueryClient();
  const inbox = useQuery(q.inbox);
  const products = useQuery(q.products);
  const search = useSearch({ strict: false }) as { intent?: "approve" };
  const back = useCallback(() => { void navigate({ to: "/p/$productId/campaigns/$campaignId", params: { productId, campaignId } }); }, [navigate, productId, campaignId]);
  const control = useMemo(() => ({
    items: inbox.data?.items ?? [],
    intent: search.intent ?? null,
    close: back,
    done: () => { void client.invalidateQueries(); back(); },
  }), [inbox.data, search.intent, back, client]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.metaKey || event.ctrlKey || event.altKey || document.querySelector("[role=dialog]:not([aria-modal=false])")) return;
      const target = event.target;
      if (target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      event.preventDefault();
      back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [back]);
  const product = products.data?.find((candidate) => candidate.id === productId);
  const mode = useMemo<ReviewMode>(() => ({ kind: "page", crumbs: [
    { label: product?.name ?? AREA_LABELS.product, to: "/p/$productId/week", params: { productId } },
    { label: AREA_LABELS.campaigns, to: "/p/$productId/campaigns", params: { productId } },
    { label: I.confirmReading },
  ] }), [product?.name, productId]);
  return <InboxContext.Provider value={control}><ReviewModeContext.Provider value={mode}><CampaignReadingReview /></ReviewModeContext.Provider></InboxContext.Provider>;
}

/** `/inbox/campaign-readings/:id` from before the reading had its own page: the campaign's product decides where it opens. */
export function CampaignReadingRedirect() {
  const { campaignId } = useParams({ strict: false }) as { campaignId: string };
  const navigate = useNavigate();
  const campaign = useQuery(q.campaign(campaignId));
  const done = useRef(false);
  const productId = campaign.data?.campaign.product_id;
  useEffect(() => {
    if (!productId || done.current) return;
    done.current = true;
    void navigate({ to: "/p/$productId/campaigns/$campaignId/reading", params: { productId, campaignId }, replace: true });
  }, [navigate, campaignId, productId]);
  return campaign.error ? <ErrorText error={campaign.error} /> : <Page crumbs={[]}><Skeleton shape="object" /></Page>;
}
